import {
  Button,
  Checkbox,
  Input,
  Modal,
  Popconfirm,
  Select,
  Table,
  Tag,
  message,
} from "antd";
import axios from "axios";
import React, { useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { GRADE_OPTIONS } from "../../../../../helper/assessmentOptions";
import { serverUrl } from "../../../../../nestserver";
import CombinedProgressCardModal from "../../../../../components/CombinedProgressCardView";

// Endpoint that lists the classes this role may see.
const CLASSES_URL = "/classes/";
const MIN_TERMS = 2;

const authHeaders = () => ({
  Authorization: `Bearer ${window.localStorage.getItem("jwt-token")}`,
});

const STATUS_COLOR = {
  draft: "default",
  ready: "green",
  notified: "blue",
  acknowledged: "purple",
};

// "12", not "12.0", but keeps the half in "11.5". Half days count as 0.5.
const days = (value) => (Number.isInteger(value) ? value : value?.toFixed(1));

/**
 * Year-end card that merges two or more of a class's progress cards (any
 * templates of the same class and year, whatever they are called). Generate
 * builds a draft per student, then the usual ready -> notify steps apply.
 */
const CombineCard = () => {
  const queryClient = useQueryClient();
  const [classId, setClassId] = useState();
  // Key of the chosen combination: its id, or "legacy-<n>" for a set that was
  // combined before names existed.
  const [comboKey, setComboKey] = useState();
  // New / rename dialog: { mode: "create" | "rename", id?, name, templateIds }.
  const [dialog, setDialog] = useState(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [busy, setBusy] = useState(false);
  const [viewId, setViewId] = useState(null);
  // Overall grade / remark saves, run one after another. Mark Ready and Notify
  // wait on this, because clicking a button blurs the remark box and starts a
  // save that the button's own request would otherwise outrun.
  const pendingSave = useRef(Promise.resolve());

  const classes = useQuery("combine-classes", async () => {
    const res = await axios.get(serverUrl + CLASSES_URL, {
      headers: authHeaders(),
    });
    return res.data;
  });

  const templates = useQuery(
    ["progress-card-template", classId],
    async () => {
      const res = await axios.get(
        `${serverUrl}/progress-card-template-v2/?class=${classId}`,
        { headers: authHeaders() },
      );
      return res.data;
    },
    { enabled: !!classId },
  );

  const combinationsKey = ["progress-card-combinations", classId];
  const combinations = useQuery(
    combinationsKey,
    async () => {
      const res = await axios.get(
        `${serverUrl}/progress-card-combined-v2/combinations?class=${classId}`,
        { headers: authHeaders() },
      );
      return res.data?.result ?? [];
    },
    { enabled: !!classId },
  );
  const comboList = (combinations.data ?? []).map((c, index) => ({
    ...c,
    key: c.id || `legacy-${index}`,
  }));
  const selectedCombo = comboList.find((c) => c.key === comboKey);
  const templateIds = selectedCombo?.template_ids ?? [];

  // A saved set that no longer exists (deleted) must not stay selected.
  useEffect(() => {
    if (comboKey && combinations.isSuccess && !selectedCombo) {
      setComboKey(undefined);
    }
  }, [comboKey, combinations.isSuccess, selectedCombo]);

  const canCombine = templateIds.length >= MIN_TERMS;
  const listKey = ["progress-card-combined", templateIds.slice().sort().join(",")];
  const combined = useQuery(
    listKey,
    async () => {
      const res = await axios.get(
        `${serverUrl}/progress-card-combined-v2/?templateIds=${templateIds.join(",")}`,
        { headers: authHeaders() },
      );
      return res.data;
    },
    { enabled: canCombine },
  );
  const rows = canCombine ? combined?.data?.result ?? [] : [];
  const refresh = () => queryClient.invalidateQueries(listKey);

  const run = async (request, onSuccess) => {
    setBusy(true);
    try {
      const res = await request();
      onSuccess?.(res?.data?.result);
      await refresh();
      await queryClient.invalidateQueries(combinationsKey);
    } catch (err) {
      const errors = err?.response?.data?.error?.errors;
      message.error(
        (Array.isArray(errors) ? errors[0] : errors) ||
          err?.response?.data?.message ||
          "Something went wrong",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = () =>
    run(
      () =>
        axios.post(
          `${serverUrl}/progress-card-combined-v2/generate`,
          { templateIds },
          { headers: authHeaders() },
        ),
      (result) => message.success(result?.message || "Generated"),
    );

  const isSent = (r) => r.status === "notified" || r.status === "acknowledged";
  // What the header checkbox selects: drafts with an overall grade and remark
  // (ready for Mark Ready) and ready cards (ready for Notify).
  const isSelectable = (r) =>
    (r.status === "draft" &&
      !!String(r.overall_grade ?? "").trim() &&
      !!String(r.overall_comment ?? "").trim()) ||
    r.status === "ready";
  const selectableKeys = rows.filter(isSelectable).map((r) => r._id);
  const allSelected =
    selectableKeys.length > 0 &&
    selectableKeys.every((id) => selectedRowKeys.includes(id));

  const handleSelectAll = async (checked) => {
    if (!checked) {
      setSelectedRowKeys([]);
      return;
    }
    // A remark just typed may still be saving; pick from the saved values.
    await pendingSave.current;
    const fresh = queryClient.getQueryData(listKey)?.result ?? rows;
    setSelectedRowKeys(fresh.filter(isSelectable).map((r) => r._id));
  };

  const draftIds = rows
    .filter((r) => selectedRowKeys.includes(r._id) && r.status === "draft")
    .map((r) => r._id);
  const readyIds = rows
    .filter((r) => selectedRowKeys.includes(r._id) && r.status === "ready")
    .map((r) => r._id);

  const handleMarkReady = () =>
    run(
      async () => {
        await pendingSave.current;
        return axios.patch(
          `${serverUrl}/progress-card-combined-v2/mark-ready`,
          { progress_card_ids: draftIds },
          { headers: authHeaders() },
        );
      },
      (result) => {
        (result?.success ? message.success : message.warning)(result?.message);
        setSelectedRowKeys([]);
      },
    );

  const handleNotify = () =>
    run(
      async () => {
        await pendingSave.current;
        return axios.patch(
          `${serverUrl}/progress-card-combined-v2/notify`,
          { progress_card_ids: readyIds },
          { headers: authHeaders() },
        );
      },
      (result) => {
        message.success(result?.message);
        setSelectedRowKeys([]);
      },
    );

  const saveOverall = (id, patch) => {
    pendingSave.current = pendingSave.current.then(async () => {
      try {
        await axios.put(
          `${serverUrl}/progress-card-combined-v2/${id}`,
          patch,
          { headers: authHeaders() },
        );
        await refresh();
      } catch (err) {
        const errors = err?.response?.data?.error?.errors;
        message.error(
          (Array.isArray(errors) ? errors[0] : errors) ||
            err?.response?.data?.message ||
            "Could not save",
        );
      }
    });
    return pendingSave.current;
  };

  const openCreate = (templateIdsPreset = [], name = "") =>
    setDialog({ mode: "create", name, templateIds: templateIdsPreset });

  const submitDialog = async () => {
    const name = (dialog?.name || "").trim();
    if (!name) {
      message.error("Give the combination a name");
      return;
    }
    if (dialog.mode === "create" && dialog.templateIds.length < MIN_TERMS) {
      message.error(`Choose at least ${MIN_TERMS} templates`);
      return;
    }
    setBusy(true);
    try {
      if (dialog.mode === "create") {
        const res = await axios.post(
          `${serverUrl}/progress-card-combined-v2/combinations`,
          { name, templateIds: dialog.templateIds },
          { headers: authHeaders() },
        );
        await queryClient.invalidateQueries(combinationsKey);
        setComboKey(res.data?.result?.id);
        message.success("Combination saved");
      } else {
        await axios.put(
          `${serverUrl}/progress-card-combined-v2/combinations/${dialog.id}`,
          { name },
          { headers: authHeaders() },
        );
        await queryClient.invalidateQueries(combinationsKey);
        message.success("Combination renamed");
      }
      setDialog(null);
    } catch (err) {
      const errors = err?.response?.data?.error?.errors;
      message.error(
        (Array.isArray(errors) ? errors[0] : errors) ||
          err?.response?.data?.message ||
          "Something went wrong",
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteCombination = async () => {
    try {
      await axios.delete(
        `${serverUrl}/progress-card-combined-v2/combinations/${selectedCombo.id}`,
        { headers: authHeaders() },
      );
      message.success("Combination deleted");
      setComboKey(undefined);
      await queryClient.invalidateQueries(combinationsKey);
    } catch (err) {
      const errors = err?.response?.data?.error?.errors;
      message.error(
        (Array.isArray(errors) ? errors[0] : errors) ||
          "Could not delete the combination",
      );
    }
  };

  const handleDownload = async (record) => {
    try {
      const res = await axios.get(
        `${serverUrl}/progress-card-combined-v2/${record._id}/download`,
        { headers: authHeaders(), responseType: "blob" },
      );
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `AnnualProgressCard_${record.name || "student"}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      message.error("Could not download the progress card");
    }
  };

  const columns = [
    {
      title: "Student",
      key: "student",
      render: (record) => (
        <div>
          <div className="font-semibold capitalize">{record.name}</div>
          <div className="text-xs text-gray-500">{record.unique_id}</div>
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      align: "center",
      render: (status) => (
        <Tag color={STATUS_COLOR[status]} className="capitalize">
          {status}
        </Tag>
      ),
    },
    {
      title: "Total Marks",
      key: "total",
      align: "center",
      render: (record) => (
        <span>
          {record.total_marks}
          {record.max_marks ? (
            <span className="text-gray-500"> / {record.max_marks}</span>
          ) : null}
        </span>
      ),
    },
    {
      title: "Rank",
      key: "rank",
      align: "center",
      render: (record) =>
        record.rank ? `${record.rank} / ${record.out_of}` : "--",
    },
    {
      title: "Attendance",
      key: "attendance",
      align: "center",
      render: (record) =>
        record.attendance
          ? `${days(record.attendance.present_days)}P / ${days(
              record.attendance.absent_days,
            )}A of ${days(record.attendance.total_days)} days · ${
              record.attendance.percent
            }%`
          : "--",
    },
    {
      title: "Overall Grade",
      key: "overall_grade",
      align: "center",
      width: 130,
      render: (record) => (
        <Select
          style={{ width: 100 }}
          placeholder="Grade"
          allowClear
          disabled={record.status === "notified" || record.status === "acknowledged"}
          value={record.overall_grade || undefined}
          options={GRADE_OPTIONS.map((g) => ({ label: g, value: g }))}
          onChange={(value) => saveOverall(record._id, { overall_grade: value ?? "" })}
        />
      ),
    },
    {
      title: "Overall Remark",
      key: "overall_comment",
      width: 280,
      render: (record) => (
        <Input.TextArea
          key={`${record._id}-${record.overall_comment}`}
          autoSize={{ minRows: 2, maxRows: 5 }}
          placeholder="Overall remark"
          defaultValue={record.overall_comment}
          disabled={record.status === "notified" || record.status === "acknowledged"}
          onBlur={(e) => {
            if (e.target.value !== (record.overall_comment || "")) {
              saveOverall(record._id, { overall_comment: e.target.value });
            }
          }}
        />
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      render: (record) => (
        <div className="flex gap-2 justify-center">
          <Button size="small" onClick={() => setViewId(record._id)}>
            View
          </Button>
          <Button size="small" onClick={() => handleDownload(record)}>
            Download
          </Button>
        </div>
      ),
    },
  ];

  const templateOptions = (templates?.data?.result ?? []).map((t) => ({
    value: t._id,
    label: `${t.name} (${t.year})`,
  }));

  return (
    <div className="p-4">
      <CombinedProgressCardModal id={viewId} onClose={() => setViewId(null)} />
      <Modal
        open={!!dialog}
        title={
          dialog?.mode === "rename" ? "Rename combination" : "New combination"
        }
        okText="Save"
        confirmLoading={busy}
        onOk={submitDialog}
        onCancel={() => setDialog(null)}
        destroyOnClose
      >
        <div className="pb-4">
          <div className="pb-1 font-semibold">Name</div>
          <Input
            placeholder="e.g. Annual Report 2026-27"
            value={dialog?.name}
            onChange={(e) => setDialog({ ...dialog, name: e.target.value })}
          />
        </div>
        {dialog?.mode === "create" ? (
          <div>
            <div className="pb-1 font-semibold">
              Templates (choose {MIN_TERMS} or more)
            </div>
            <Select
              mode="multiple"
              className="w-full"
              placeholder="Select templates"
              value={dialog.templateIds}
              options={templateOptions}
              optionFilterProp="label"
              onChange={(value) => setDialog({ ...dialog, templateIds: value })}
            />
          </div>
        ) : null}
      </Modal>
      <div className="bg-white rounded-2xl p-6">
        <h3 className="text-lg font-bold">Combine Progress Cards</h3>
        <p className="text-gray-500 pb-4">
          Save a named combination of {MIN_TERMS} or more templates of one
          class and year (for example Quarterly, Half-yearly and Annual), then
          pick it by name. Each student with a ready or published card in
          every template gets one combined year-end card.
        </p>

        <div className="grid grid-cols-2 gap-4 pb-4">
          <Select
            showSearch
            placeholder="Select class"
            optionFilterProp="children"
            value={classId}
            onChange={(value) => {
              setClassId(value);
              setComboKey(undefined);
              setSelectedRowKeys([]);
            }}
          >
            {(classes?.data ?? []).map((c) => (
              <Select.Option key={c._id} value={c._id}>
                {c.name}
              </Select.Option>
            ))}
          </Select>
          <div className="flex gap-2">
            <Select
              className="flex-1"
              placeholder="Select a saved combination"
              disabled={!classId}
              loading={combinations.isFetching}
              value={comboKey}
              onChange={(value) => {
                setComboKey(value);
                setSelectedRowKeys([]);
              }}
              options={comboList.map((c) => ({
                value: c.key,
                label: c.legacy
                  ? `${c.name} (not named yet)`
                  : `${c.name}${c.year ? ` · ${c.year}` : ""}`,
              }))}
            />
            <Button disabled={!classId} onClick={() => openCreate()}>
              New
            </Button>
          </div>
        </div>

        {selectedCombo ? (
          <div className="flex flex-wrap items-center gap-3 pb-4">
            <span className="text-gray-600">
              {selectedCombo.template_names.join("  +  ")}
              {selectedCombo.card_count
                ? `  ·  ${selectedCombo.card_count} cards`
                : ""}
            </span>
            {selectedCombo.legacy ? (
              <Button
                size="small"
                onClick={() =>
                  openCreate(selectedCombo.template_ids, selectedCombo.name)
                }
              >
                Save with a name
              </Button>
            ) : selectedCombo.card_count > 0 ? null : (
              <>
                <Button
                  size="small"
                  onClick={() =>
                    setDialog({
                      mode: "rename",
                      id: selectedCombo.id,
                      name: selectedCombo.name,
                      templateIds: selectedCombo.template_ids,
                    })
                  }
                >
                  Rename
                </Button>
                <Popconfirm
                  title="Delete this combination?"
                  description="Only possible while no combined cards use it."
                  onConfirm={handleDeleteCombination}
                >
                  <Button size="small" danger>
                    Delete
                  </Button>
                </Popconfirm>
              </>
            )}
          </div>
        ) : null}

        <div className="flex flex-wrap gap-3 pb-4">
          <Button
            type="primary"
            disabled={!canCombine || busy || selectedCombo?.pending_count === 0}
            loading={busy}
            onClick={handleGenerate}
          >
            Generate Combined Cards
          </Button>
          <Button
            disabled={!draftIds.length || busy}
            onClick={handleMarkReady}
          >
            Mark Ready{draftIds.length ? ` (${draftIds.length})` : ""}
          </Button>
          <Button disabled={!readyIds.length || busy} onClick={handleNotify}>
            Notify Parents{readyIds.length ? ` (${readyIds.length})` : ""}
          </Button>
        </div>

        <Table
          rowKey="_id"
          columns={columns}
          dataSource={rows}
          loading={combined.isFetching}
          pagination={false}
          rowSelection={{
            selectedRowKeys,
            onChange: setSelectedRowKeys,
            // Replaces antd's own select-all, which would also pick drafts that
            // are still missing a grade or remark.
            columnTitle: (
              <Checkbox
                checked={allSelected}
                indeterminate={!allSelected && selectedRowKeys.length > 0}
                disabled={!selectableKeys.length}
                onChange={(e) => handleSelectAll(e.target.checked)}
              />
            ),
            getCheckboxProps: (record) => ({ disabled: isSent(record) }),
          }}
          locale={{
            emptyText: canCombine
              ? "No combined cards yet. Click Generate Combined Cards."
              : "Choose a class and a saved combination, or add a new one.",
          }}
        />
      </div>
    </div>
  );
};

export default CombineCard;
